/**
 * BUDB PKR — Slot symbols
 * Pay symbols = SVG art; Grudge wild / bonus / jackpots = logo icons.
 */

import React from "react";
import { JACKPOT_TIERS, type JackpotTierId } from "@shared/slot-engine";

export type SymbolId =
  | "skull"
  | "gbux"
  | "crown"
  | "diamond"
  | "wolf"
  | "ace"
  | "dice"
  | "revolver"
  | "wild"
  | "scatter"
  | "bonus"
  | "jp_mini"
  | "jp_medium"
  | "jp_large"
  | "jp_grand";

export const SYMBOL_LABELS: Record<SymbolId, string> = {
  skull: "Sea Wolf",
  gbux: "GBUX",
  crown: "Pirate Crown",
  diamond: "Ethereal Gem",
  wolf: "Wolf",
  ace: "Ace",
  dice: "Dice",
  revolver: "Armory",
  wild: "Racalvin Wild",
  scatter: "Malachar Bounty",
  bonus: "Ethereal Seal",
  jp_mini: "Wanted Durin",
  jp_medium: "Wanted Sylara",
  jp_large: "Wanted Kael",
  jp_grand: "Wanted Racalvin",
};

/** Official GBUX coin art (transparent) */
export const GBUX_COIN_SRC = "/media/gbux-coin.png";
export const GBUX_COIN_SLOT = "/slots/icons/gbux.png";

/**
 * Racalvin Ethereal Treasure — 5×5 generation pack under icons/v5
 * (generated square assets for denser reels). Falls back to legacy paths.
 */
/** Transparent PNG plates (light/white/blue plates removed) — fill cells cleanly */
export const ICON_SRC: Partial<Record<SymbolId, string>> = {
  skull: "/slots/icons/v5/skull.png",
  crown: "/slots/icons/v5/crown.png",
  revolver: "/slots/icons/v5/revolver.png",
  wolf: "/slots/icons/v5/wolf.png",
  diamond: "/slots/icons/v5/diamond.png",
  gbux: "/slots/icons/v5/gbux.png",
  ace: "/slots/icons/v5/ace.png",
  dice: "/slots/icons/v5/dice.png",
  wild: "/slots/icons/v5/wild.png",
  scatter: "/slots/bounty/malachar-icon.png",
  bonus: "/slots/icons/v5/bonus.png",
  jp_mini: JACKPOT_TIERS.jp_mini.icon,
  jp_medium: JACKPOT_TIERS.jp_medium.icon,
  jp_large: JACKPOT_TIERS.jp_large.icon,
  jp_grand: JACKPOT_TIERS.jp_grand.icon,
};

// ── Pay symbol SVGs (compact) ─────────────────────────────────────────────────

const Skull = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="sg-skull-bg" cx="50%" cy="40%" r="55%">
        <stop offset="0%" stopColor="#7C3AED" />
        <stop offset="100%" stopColor="#1E0A3C" />
      </radialGradient>
    </defs>
    <rect width="80" height="80" rx="10" fill="url(#sg-skull-bg)" />
    <path d="M20 28 L26 16 L32 24 L40 12 L48 24 L54 16 L60 28 Z" fill="#F59E0B" />
    <ellipse cx="40" cy="44" rx="20" ry="18" fill="#E2D9F3" />
    <ellipse cx="32" cy="42" rx="6" ry="7" fill="#1E0A3C" />
    <ellipse cx="48" cy="42" rx="6" ry="7" fill="#1E0A3C" />
    <path d="M37 52 L40 49 L43 52 L41 55 L39 55 Z" fill="#1E0A3C" />
    <rect x="28" y="57" width="24" height="4" rx="2" fill="#C4B5D4" />
    <rect width="80" height="80" rx="10" fill="none" stroke="#F59E0B" strokeWidth="2" opacity="0.6" />
  </svg>
);

const Crown = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <rect width="80" height="80" rx="10" fill="#1C1700" />
    <path d="M14 54 L14 30 L25 44 L40 18 L55 44 L66 30 L66 54 Z" fill="#FCD34D" />
    <rect x="14" y="54" width="52" height="12" rx="3" fill="#B45309" />
    <circle cx="40" cy="20" r="5" fill="#3B82F6" />
    <rect width="80" height="80" rx="10" fill="none" stroke="#FCD34D" strokeWidth="2" opacity="0.5" />
  </svg>
);

const Diamond = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <rect width="80" height="80" rx="10" fill="#0A1628" />
    <polygon points="40,12 64,33 40,68 16,33" fill="#38BDF8" />
    <polygon points="28,20 36,25 32,33" fill="white" opacity="0.35" />
    <rect width="80" height="80" rx="10" fill="none" stroke="#38BDF8" strokeWidth="2" opacity="0.4" />
  </svg>
);

const Wolf = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <rect width="80" height="80" rx="10" fill="#0C1A30" />
    <circle cx="58" cy="20" r="12" fill="#FEF3C7" opacity="0.9" />
    <polygon points="22,42 28,20 34,38" fill="#94A3B8" />
    <polygon points="46,38 52,20 58,42" fill="#94A3B8" />
    <ellipse cx="40" cy="46" rx="18" ry="16" fill="#CBD5E1" />
    <circle cx="33" cy="43" r="4" fill="#F59E0B" />
    <circle cx="47" cy="43" r="4" fill="#F59E0B" />
    <rect width="80" height="80" rx="10" fill="none" stroke="#94A3B8" strokeWidth="2" opacity="0.4" />
  </svg>
);

const Ace = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <rect width="80" height="80" rx="10" fill="#030712" />
    <rect x="14" y="8" width="52" height="64" rx="6" fill="#F8FAFC" />
    <text x="19" y="26" fill="#0F172A" fontSize="13" fontWeight="700">A</text>
    <path d="M40 22 C40 22 28 32 28 40 C28 45 32 47 36 45 C34 48 33 51 30 53 L50 53 C47 51 46 48 44 45 C48 47 52 45 52 40 C52 32 40 22 40 22 Z" fill="#0F172A" />
  </svg>
);

const DiceSymbol = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <rect width="80" height="80" rx="10" fill="#1C0A0A" />
    <rect x="12" y="28" width="40" height="40" rx="7" fill="#EF4444" />
    <circle cx="23" cy="38" r="3.5" fill="white" />
    <circle cx="23" cy="48" r="3.5" fill="white" />
    <circle cx="23" cy="58" r="3.5" fill="white" />
    <circle cx="41" cy="38" r="3.5" fill="white" />
    <circle cx="41" cy="48" r="3.5" fill="white" />
    <circle cx="41" cy="58" r="3.5" fill="white" />
  </svg>
);

const Revolver = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <rect width="80" height="80" rx="10" fill="#0C0A09" />
    <rect x="14" y="32" width="44" height="10" rx="3" fill="#A8A29E" />
    <circle cx="36" cy="44" r="12" fill="#57534E" />
    <path d="M20 42 L28 44 L22 68 Q18 70 16 66 Z" fill="#292524" />
  </svg>
);

/** Image-based special / jackpot / coin cell — fill cell, no plate color */
function IconTile({
  src,
  label,
  glow,
  badge,
}: {
  src: string;
  label: string;
  glow?: string;
  badge?: string;
}) {
  return (
    <div
      className="slots-icon-tile relative w-full h-full overflow-hidden flex items-center justify-center"
      style={{
        background: "transparent",
        boxShadow: glow
          ? `inset 0 0 0 1.5px ${glow}99, 0 0 10px ${glow}40`
          : "none",
        borderRadius: 8,
      }}
      title={label}
    >
      <img
        src={src}
        alt={label}
        draggable={false}
        className="slots-icon-img"
      />
      {badge && (
        <span
          className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[7px] sm:text-[8px] font-black tracking-wider px-1 rounded z-[1]"
          style={{
            background: "rgba(0,0,0,0.72)",
            color: glow || "#fbbf24",
            border: `1px solid ${glow || "#fbbf24"}66`,
          }}
        >
          {badge}
        </span>
      )}
    </div>
  );
}

function GrudgeWildIcon({ multiplier = 1 }: { multiplier?: number }) {
  const glow =
    multiplier >= 3 ? "#f97316" : multiplier >= 2 ? "#a855f7" : "#fbbf24";
  return (
    <IconTile
      src={ICON_SRC.wild!}
      label="Racalvin Wild"
      glow={glow}
      badge={multiplier > 1 ? `${multiplier}× WILD` : "WILD"}
    />
  );
}

function BonusIcon() {
  return (
    <IconTile
      src={ICON_SRC.bonus!}
      label="Ethereal Seal"
      glow="#34d399"
      badge="BONUS"
    />
  );
}

function ImagePaySymbol({ id }: { id: SymbolId }) {
  const src = ICON_SRC[id];
  if (!src) return null;
  return (
    <IconTile
      src={src}
      label={SYMBOL_LABELS[id]}
      glow={
        id === "skull"
          ? "#f59e0b"
          : id === "crown"
            ? "#fcd34d"
            : id === "revolver"
              ? "#f87171"
              : id === "diamond"
                ? "#38bdf8"
                : id === "wolf"
                  ? "#94a3b8"
                  : undefined
      }
    />
  );
}

function JackpotIcon({ tier }: { tier: JackpotTierId }) {
  const t = JACKPOT_TIERS[tier];
  return (
    <IconTile
      src={t.icon}
      label={`${t.name} Jackpot`}
      glow={t.color}
      badge={t.name.toUpperCase()}
    />
  );
}

const GbuxToken = () => (
  <IconTile
    src={ICON_SRC.gbux || GBUX_COIN_SLOT}
    label="GBUX"
    glow="#22d3ee"
    badge="GBUX"
  />
);

const CoinScatter = () => (
  <IconTile
    src={ICON_SRC.scatter || GBUX_COIN_SLOT}
    label="Lord Malachar · Minion Bounty"
    glow="#4ade80"
    badge="BOUNTY"
  />
);

// ── Main export ───────────────────────────────────────────────────────────────

interface SlotSymbolProps {
  id: SymbolId | string;
  wildMultiplier?: number;
  className?: string;
  isWinning?: boolean;
}

export function SlotSymbol({
  id,
  wildMultiplier = 1,
  className = "",
  isWinning = false,
}: SlotSymbolProps) {
  const ring = isWinning
    ? "slots-sym-win ring-2 ring-yellow-400 rounded-lg shadow-[0_0_14px_3px_rgba(251,191,36,0.65)]"
    : "";
  const label = SYMBOL_LABELS[id as SymbolId] ?? id;
  const sid = id as SymbolId;

  return (
    <div
      className={`w-full h-full slots-sym ${isWinning ? "slots-sym--win" : ""} ${ring} ${className}`}
      title={label}
    >
      {id === "wild" && <GrudgeWildIcon multiplier={wildMultiplier} />}
      {id === "scatter" && <CoinScatter />}
      {id === "bonus" && <BonusIcon />}
      {id === "jp_mini" && <JackpotIcon tier="jp_mini" />}
      {id === "jp_medium" && <JackpotIcon tier="jp_medium" />}
      {id === "jp_large" && <JackpotIcon tier="jp_large" />}
      {id === "jp_grand" && <JackpotIcon tier="jp_grand" />}
      {id === "gbux" && <GbuxToken />}
      {ICON_SRC[sid] &&
        id !== "wild" &&
        id !== "scatter" &&
        id !== "bonus" &&
        id !== "gbux" &&
        !String(id).startsWith("jp_") && <ImagePaySymbol id={sid} />}
      {/* SVG fallbacks if image missing */}
      {!ICON_SRC[sid] && id === "ace" && <Ace />}
      {!ICON_SRC[sid] && id === "dice" && <DiceSymbol />}
      {!ICON_SRC[sid] && id === "skull" && <Skull />}
      {!ICON_SRC[sid] && id === "crown" && <Crown />}
      {!ICON_SRC[sid] && id === "diamond" && <Diamond />}
      {!ICON_SRC[sid] && id === "wolf" && <Wolf />}
      {!ICON_SRC[sid] && id === "revolver" && <Revolver />}
    </div>
  );
}

export function JackpotTierBadge({
  tier,
  compact,
  count,
  pulse,
  badgeRef,
}: {
  tier: JackpotTierId;
  compact?: boolean;
  /** Session / spin collect count for this Wanted */
  count?: number;
  pulse?: boolean;
  badgeRef?: React.Ref<HTMLDivElement>;
}) {
  const t = JACKPOT_TIERS[tier];
  return (
    <div
      ref={badgeRef}
      data-wanted-tier={tier}
      className={`slots-jp-badge flex items-center gap-1.5 rounded-lg px-1.5 py-1 min-w-0${
        pulse ? " slots-jp-badge--pulse" : ""
      }`}
      title={`Most Wanted · ${t.name} — ${t.title} · ${t.mult}× bet`}
      style={{
        background: "rgba(0,0,0,0.45)",
        border: `1px solid ${t.color}55`,
      }}
    >
      <div className="relative shrink-0">
        <img
          src={t.icon}
          alt={t.name}
          className={
            compact
              ? "w-6 h-6 rounded object-cover shrink-0"
              : "w-10 h-10 rounded object-cover shrink-0"
          }
          draggable={false}
        />
        {typeof count === "number" && count > 0 ? (
          <span
            className="slots-jp-badge__count"
            style={{ borderColor: t.color, color: t.color }}
          >
            {count > 99 ? "99+" : count}
          </span>
        ) : null}
      </div>
      <div className="text-left leading-tight min-w-0">
        <div
          className="text-[9px] font-bold uppercase truncate"
          style={{ color: t.color }}
        >
          {t.shortName}
        </div>
        {!compact && (
          <>
            <div className="text-[10px] text-white/55 truncate">{t.title}</div>
            <div className="text-[11px] text-white/70 font-mono">{t.mult}× bet</div>
          </>
        )}
      </div>
    </div>
  );
}
